import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import { WsProvider } from '@/lib/WsContext';
import { Toaster } from '@/components/ui/sonner';
import LoginPage from '@/pages/LoginPage';
import OwnerDashboard from '@/pages/owner/OwnerDashboard';
import OwnerProducts from '@/pages/owner/OwnerProducts';
import OwnerUsers from '@/pages/owner/OwnerUsers';
import OwnerKpi from '@/pages/owner/OwnerKpi';
import OwnerReports from '@/pages/owner/OwnerReports';
import OwnerLeaderboard from '@/pages/owner/OwnerLeaderboard';
import OwnerSuppliers from '@/pages/owner/OwnerSuppliers';
import OwnerCustomers from '@/pages/owner/OwnerCustomers';
import OwnerOutstanding from '@/pages/owner/OwnerOutstanding';
import OwnerSettings from '@/pages/owner/OwnerSettings';
import OwnerTransactions from '@/pages/owner/OwnerTransactions';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminSuppliers from '@/pages/admin/AdminSuppliers';
import AdminCustomers from '@/pages/admin/AdminCustomers';
import AdminOutstanding from '@/pages/admin/AdminOutstanding';
import AdminProducts from '@/pages/admin/AdminProducts';
import AdminDocuments from '@/pages/admin/AdminDocuments';
import AdminReports from '@/pages/admin/AdminReports';
import AdminLeaderboard from '@/pages/admin/AdminLeaderboard';
import AdminTransactions from '@/pages/admin/AdminTransactions';
import KasirPOS from '@/pages/kasir/KasirPOS';
import KasirProducts from '@/pages/kasir/KasirProducts';
import KasirCustomers from '@/pages/kasir/KasirCustomers';
import KasirOutstanding from '@/pages/kasir/KasirOutstanding';
import KasirHistory from '@/pages/kasir/KasirHistory';
import KasirDocuments from '@/pages/kasir/KasirDocuments';
import SalesmanDashboard from '@/pages/salesman/SalesmanDashboard';
import SalesmanCatalog from '@/pages/salesman/SalesmanCatalog';
import SalesmanLeaderboard from '@/pages/salesman/SalesmanLeaderboard';
import SalesmanAccount from '@/pages/salesman/SalesmanAccount';
import FinanceDashboard from '@/pages/finance/FinanceDashboard';
import FinanceOperational from '@/pages/finance/FinanceOperational';
import FinanceReports from '@/pages/finance/FinanceReports';
import OwnerExpenses from '@/pages/owner/OwnerExpenses';
import OwnerEmployees from '@/pages/owner/OwnerEmployees';
import OwnerSalary from '@/pages/owner/OwnerSalary';
import OwnerCompanyExpenses from '@/pages/owner/OwnerCompanyExpenses';
import AdminEmployees from '@/pages/admin/AdminEmployees';
import OwnerPrintTemplates from '@/pages/owner/OwnerPrintTemplates';
import OwnerProductAssets from '@/pages/owner/OwnerProductAssets';
import OwnerIronPrices from '@/pages/owner/OwnerIronPrices';

function RoleRoute({ children, roles }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  return children;
}

export function homeFor(role) {
  switch (role) {
    case 'owner': return '/owner';
    case 'admin': return '/admin';
    case 'kasir': return '/kasir';
    case 'salesman': return '/salesman';
    case 'finance': return '/finance';
    default: return '/login';
  }
}

function RootRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homeFor(user.role)} replace />;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <WsProvider>
            <Toaster position="top-right" richColors closeButton />
            <Routes>
              <Route path="/login" element={<LoginPage />} />

              {/* Owner */}
              <Route path="/owner" element={<RoleRoute roles={['owner']}><OwnerDashboard /></RoleRoute>} />
              <Route path="/owner/products" element={<RoleRoute roles={['owner']}><OwnerProducts /></RoleRoute>} />
              <Route path="/owner/users" element={<RoleRoute roles={['owner']}><OwnerUsers /></RoleRoute>} />
              <Route path="/owner/kpi" element={<RoleRoute roles={['owner']}><OwnerKpi /></RoleRoute>} />
              <Route path="/owner/reports" element={<RoleRoute roles={['owner']}><OwnerReports /></RoleRoute>} />
              <Route path="/owner/leaderboard" element={<RoleRoute roles={['owner']}><OwnerLeaderboard /></RoleRoute>} />
              <Route path="/owner/suppliers" element={<RoleRoute roles={['owner']}><OwnerSuppliers /></RoleRoute>} />
              <Route path="/owner/customers" element={<RoleRoute roles={['owner']}><OwnerCustomers /></RoleRoute>} />
              <Route path="/owner/outstanding" element={<RoleRoute roles={['owner']}><OwnerOutstanding /></RoleRoute>} />
              <Route path="/owner/transactions" element={<RoleRoute roles={['owner']}><OwnerTransactions /></RoleRoute>} />
              <Route path="/owner/expenses" element={<RoleRoute roles={['owner']}><OwnerExpenses /></RoleRoute>} />
              <Route path="/owner/employees" element={<RoleRoute roles={['owner']}><OwnerEmployees /></RoleRoute>} />
              <Route path="/owner/salary" element={<RoleRoute roles={['owner']}><OwnerSalary /></RoleRoute>} />
              <Route path="/owner/company-expenses" element={<RoleRoute roles={['owner']}><OwnerCompanyExpenses /></RoleRoute>} />
              <Route path="/owner/product-assets" element={<RoleRoute roles={['owner']}><OwnerProductAssets /></RoleRoute>} />
              <Route path="/owner/iron-prices" element={<RoleRoute roles={['owner']}><OwnerIronPrices /></RoleRoute>} />
              <Route path="/owner/print-templates" element={<RoleRoute roles={['owner']}><OwnerPrintTemplates /></RoleRoute>} />
              <Route path="/owner/settings" element={<RoleRoute roles={['owner']}><OwnerSettings /></RoleRoute>} />

              {/* Admin */}
              <Route path="/admin" element={<RoleRoute roles={['admin']}><AdminDashboard /></RoleRoute>} />
              <Route path="/admin/suppliers" element={<RoleRoute roles={['admin']}><AdminSuppliers /></RoleRoute>} />
              <Route path="/admin/customers" element={<RoleRoute roles={['admin']}><AdminCustomers /></RoleRoute>} />
              <Route path="/admin/outstanding" element={<RoleRoute roles={['admin']}><AdminOutstanding /></RoleRoute>} />
              <Route path="/admin/products" element={<RoleRoute roles={['admin']}><AdminProducts /></RoleRoute>} />
              <Route path="/admin/transactions" element={<RoleRoute roles={['admin']}><AdminTransactions /></RoleRoute>} />
              <Route path="/admin/employees" element={<RoleRoute roles={['admin']}><AdminEmployees /></RoleRoute>} />
              <Route path="/admin/documents" element={<RoleRoute roles={['admin']}><AdminDocuments /></RoleRoute>} />
              <Route path="/admin/reports" element={<RoleRoute roles={['admin']}><AdminReports /></RoleRoute>} />
              <Route path="/admin/leaderboard" element={<RoleRoute roles={['admin']}><AdminLeaderboard /></RoleRoute>} />

              {/* Kasir */}
              <Route path="/kasir" element={<RoleRoute roles={['kasir']}><KasirPOS /></RoleRoute>} />
              <Route path="/kasir/products" element={<RoleRoute roles={['kasir']}><KasirProducts /></RoleRoute>} />
              <Route path="/kasir/customers" element={<RoleRoute roles={['kasir']}><KasirCustomers /></RoleRoute>} />
              <Route path="/kasir/outstanding" element={<RoleRoute roles={['kasir']}><KasirOutstanding /></RoleRoute>} />
              <Route path="/kasir/documents" element={<RoleRoute roles={['kasir']}><KasirDocuments /></RoleRoute>} />
              <Route path="/kasir/history" element={<RoleRoute roles={['kasir']}><KasirHistory /></RoleRoute>} />

              {/* Salesman */}
              <Route path="/salesman" element={<RoleRoute roles={['salesman']}><SalesmanDashboard /></RoleRoute>} />
              <Route path="/salesman/catalog" element={<RoleRoute roles={['salesman']}><SalesmanCatalog /></RoleRoute>} />
              <Route path="/salesman/leaderboard" element={<RoleRoute roles={['salesman']}><SalesmanLeaderboard /></RoleRoute>} />
              <Route path="/salesman/account" element={<RoleRoute roles={['salesman']}><SalesmanAccount /></RoleRoute>} />

              {/* Finance */}
              <Route path="/finance" element={<RoleRoute roles={['finance']}><FinanceDashboard /></RoleRoute>} />
              <Route path="/finance/operational" element={<RoleRoute roles={['finance']}><FinanceOperational /></RoleRoute>} />
              <Route path="/finance/reports" element={<RoleRoute roles={['finance']}><FinanceReports /></RoleRoute>} />

              <Route path="/" element={<RootRedirect />} />
              <Route path="*" element={<RootRedirect />} />
            </Routes>
          </WsProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
